import { SxProps, Theme } from '@mui/material';
import React, { useCallback, useContext, useMemo } from 'react';

import { ApiContext } from '_lib/api/apiContext';
import { Customer, toDisplayName } from '_lib/api/apiTypes.shim';
import { Stack } from '_lib/components/Stack/Stack';
import { Typography } from '_lib/components/Typography/Typography';
import { SearchSelect } from '_lib/featureComponents/SearchSelect/SearchSelect';

import { formatAddressOneLine } from '_lib/utils/helpers';
import { FieldIssueProps } from '_lib/utils/validationErrorUtils';

export interface CustomerNameSearchSelectProps extends FieldIssueProps {
  customer: Customer | null;
  onSetCustomer: (customer: Customer | null) => Promise<boolean>;
  companyId: string;
  required?: boolean;
  sx?: SxProps<Theme>;
}

const getCustomerLabel = (customer: Customer) => toDisplayName(customer) || 'Unknown Customer';

export const CustomerNameSearchSelect = (props: CustomerNameSearchSelectProps) => {
  const { customer, onSetCustomer, companyId, required, issuePath, issues, sx } = props;

  const api = useContext(ApiContext);
  const serviceTitanApi = useMemo(() => api.serviceTitan(companyId), [api, companyId]);

  const handleSearch = useCallback(
    async (searchValue: string) => {
      const res = await serviceTitanApi.customers.list({ params: { name: searchValue } });
      return res.results;
    },
    [serviceTitanApi.customers]
  );

  return (
    <SearchSelect
      doSearch={handleSearch}
      selectedOption={customer}
      trySetSelectedOption={onSetCustomer}
      getSelectedOptionLabel={(option) => (typeof option === 'string' ? option : getCustomerLabel(option))}
      renderListOption={(customer, liProps) => {
        return (
          <li {...liProps} key={customer.providerId}>
            <Stack direction='column' spacing={0.5}>
              <Typography variant='body1Bold' text={getCustomerLabel(customer)} />
              <Typography text={formatAddressOneLine(customer, { emptyDisplayValue: 'Unknown Billing Address' })} />
            </Stack>
          </li>
        );
      }}
      label='Customer'
      placeHolder='Customer Name'
      issuePath={issuePath}
      issues={issues}
      required={required}
      sx={sx}
    />
  );
};
